import React, { useContext, useMemo } from "react";
import clsx from "clsx";

import { Context } from "../../../../teacherStore";
import { getStudentAnswersByQuestion } from "../../../../libs/ActiveLearning";
import notFoundImg from "../../../../assets/images/ActiveLearning/not_found_theme.jpg";
import styles from "../ActiveLearningLessonAnswerData.module.scss";

interface ILesson1Pros {
  materialCode: string;
  lessonNo: number;
}

export const Lesson1: React.FC<ILesson1Pros> = ({ materialCode, lessonNo }) => {
  const { state } = useContext(Context);
  const activeLearningStudentAnswers = state.active_learning_student_answers;
  const activeLearningThemes = state.active_learning_themes;

  const answersL1I1Q1 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      1,
      1,
    );
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  const answersL1I1Q2 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      1,
      2,
    );
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  const answersL1I2Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      1,
    );

    if (answers.length > 0) {
      const [answer] = answers;
      const themeId = answer.contents.theme_id;
      return activeLearningThemes.find(theme => theme.id === themeId);
    }
  }, [
    materialCode,
    activeLearningStudentAnswers,
    activeLearningThemes,
    lessonNo,
  ]);

  const answersL1I2Q2 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
      2,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.reason).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  return (
    <div className={styles.lesson}>
      <p className={styles.title}>
        Lesson{lessonNo < 10 ? `0${lessonNo}` : lessonNo}{" "}
        やりたいことのタネを探そう
      </p>
      <div className={styles.inner}>
        <div className={styles.work}>
          <p className={styles.no}>WORK01</p>
          <div className={styles.question}>
            <p className={styles.heading}>
              自分がやりたいことや興味があることを分解しよう。
            </p>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.interest}>
                      やりたいこと/興味があること
                    </th>
                    <th className={styles.reason}>その理由</th>
                    <th className={styles.field}>関連する分野</th>
                  </tr>
                </thead>
                <tbody>
                  {answersL1I1Q1.length > 0 ? (
                    answersL1I1Q1.map(answer => (
                      <tr key={answer.id}>
                        <td className={clsx(styles.textInput, styles.interest)}>
                          {answer.contents.interest}
                        </td>
                        <td className={clsx(styles.textInput, styles.reason)}>
                          {answer.contents.reason}
                        </td>
                        <td className={clsx(styles.check, styles.field)}>
                          {answer.contents.field_categories?.map(
                            (category: string, index: number) => (
                              <span key={index} className={styles.tag}>
                                {category}
                              </span>
                            ),
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className={clsx(styles.textInput, styles.interest)}>
                        回答データがありません。
                      </td>
                      <td className={clsx(styles.textInput, styles.reason)}>
                        回答データがありません。
                      </td>
                      <td className={clsx(styles.check, styles.field)}>
                        回答データがありません。
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>
              自分にはどんな特性があるかを考えよう。
            </p>
            <div className={styles.actionCategoryList}>
              {answersL1I1Q2.length > 0
                ? answersL1I1Q2.map(answer =>
                    answer.contents.action_categories?.map(
                      (category: string, index: number) => (
                        <span key={index} className={styles.actionCategory}>
                          {category}
                        </span>
                      ),
                    ),
                  )
                : "回答データがありません。"}
            </div>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK02</p>
          <div className={styles.question}>
            <p className={styles.heading}>取り組みたいと思うテーマを選ぼう。</p>
            <div className={styles.theme}>
              <img
                className={styles.themeImage}
                src={
                  answersL1I2Q1?.enageed_theme_name
                    ? `https://admin.gear.enageed.net${answersL1I2Q1.image_url}`
                    : notFoundImg
                }
                alt="企業テーマ"
              />
              <div className={styles.themeWrapper}>
                <p className={styles.themeTitle}>
                  {answersL1I2Q1?.title || "回答データがありません。"}
                </p>
                <p className={styles.themeDesc}>{answersL1I2Q1?.desc}</p>
              </div>
            </div>
          </div>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>
              テーマを選んだ理由を入力しよう。
            </p>
            <p className={clsx(styles.answer, styles.high)}>{answersL1I2Q2}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Lesson1;
